// Markdown-import themes for /apps/pdf-tools/pdf-editor.
//
// A theme is a plain serializable object holding every style the
// markdown → PDF layout engine (md-layout.ts) understands — one group
// per markdown structure. Presets below are starting points; the
// import view deep-clones one and lets the user edit any field, so
// nothing here is ever mutated.
//
// Font families use the same id space as the rest of the editor:
//   "helvetica" | "times" | "courier"  → Base-14, nothing to embed
//   "lib:<id>"                         → lib/fonts.ts library
//   "upload:<id>"                      → session-uploaded face
// Bold/italic variants are resolved per family by the import view
// (Base-14 name suffixes / pickVariant) and shipped to the worker as
// resolved faces keyed by `faceKey(family, bold, italic)`.

export type MdAlign = "left" | "center" | "right" | "justify";

export interface MdTextStyle {
  family: string;
  /** Font size, pt. */
  size: number;
  /** CSS hex. */
  color: string;
  bold: boolean;
  italic: boolean;
  /** Baseline-to-baseline distance as a multiple of size. */
  lineHeight: number;
  align: MdAlign;
  /** Gap above/below the block, pt. */
  spaceBefore: number;
  spaceAfter: number;
}

export interface MdHeadingStyle extends MdTextStyle {
  /** Horizontal rule under the heading (GitHub h1/h2 look). */
  rule: boolean;
  ruleColor: string;
}

export interface MdCodeStyle {
  family: string;
  size: number;
  color: string;
  lineHeight: number;
  /** Block background. */
  background: string;
  /** Thin border around the block; null = none. */
  borderColor: string | null;
  /** Inner padding, pt. */
  padding: number;
  spaceBefore: number;
  spaceAfter: number;
}

export interface MdCodespanStyle {
  family: string;
  color: string;
  /** Behind-the-run tint; null = none. */
  background: string | null;
}

export interface MdBlockquoteStyle {
  barColor: string;
  barWidth: number;
  /** Quote body tint; null = none. */
  background: string | null;
  /** Text colour override for quoted content. */
  color: string;
  italic: boolean;
  /** Left inset of quoted content (bar included), pt. */
  indent: number;
  spaceBefore: number;
  spaceAfter: number;
}

export interface MdListStyle {
  /** Indent per nesting level, pt (also the marker column). */
  indent: number;
  /** Marker colour; "" = inherit the item text colour. */
  markerColor: string;
  /** Bullet glyph per level, cycling (e.g. "•◦▪"). */
  bullets: string;
  /** Extra gap between items, pt (tight lists). */
  itemGap: number;
}

export interface MdTableStyle {
  size: number;
  borderColor: string;
  borderWidth: number;
  headerBackground: string;
  headerColor: string;
  headerBold: boolean;
  /** Every-other-row tint; null = none. */
  stripeBackground: string | null;
  cellPaddingX: number;
  cellPaddingY: number;
  spaceBefore: number;
  spaceAfter: number;
}

export interface MdHrStyle {
  color: string;
  /** Thickness, pt. */
  width: number;
  spaceBefore: number;
  spaceAfter: number;
}

export interface MdLinkStyle {
  color: string;
  underline: boolean;
}

export interface MdImageStyle {
  align: "left" | "center";
  /** Max width as a fraction of the text column (0.1‥1). */
  maxWidth: number;
}

export interface MdPageStyle {
  /** Key into PAGE_PRESETS (ui-constants.ts). */
  preset: "A4" | "A5" | "Letter" | "Legal" | "Tabloid";
  landscape: boolean;
  /** Margins, pt. */
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  marginLeft: number;
  /** Page background; "#ffffff" = plain paper. */
  background: string;
}

export interface MdThemeConfig {
  page: MdPageStyle;
  /** Paragraph text — the base everything else sits around. */
  body: MdTextStyle;
  /** h1‥h6. */
  headings: [
    MdHeadingStyle,
    MdHeadingStyle,
    MdHeadingStyle,
    MdHeadingStyle,
    MdHeadingStyle,
    MdHeadingStyle,
  ];
  code: MdCodeStyle;
  codespan: MdCodespanStyle;
  blockquote: MdBlockquoteStyle;
  list: MdListStyle;
  table: MdTableStyle;
  hr: MdHrStyle;
  link: MdLinkStyle;
  image: MdImageStyle;
}

export interface MdThemePreset {
  id: string;
  label: string;
  /** One-line description shown under the label in the gallery. */
  blurb: string;
  config: MdThemeConfig;
}

/** Face id the layout engine measures with and the worker draws with. */
export function faceKey(family: string, bold: boolean, italic: boolean): string {
  return `${family}|${bold ? "b" : ""}${italic ? "i" : ""}`;
}

/** Base-14 PostScript name for the built-in family groups (null when
 *  the family is a library/uploaded face that needs bytes instead). */
export function base14Name(family: string, bold: boolean, italic: boolean): string | null {
  if (family === "helvetica" || family === "courier") {
    const root = family === "helvetica" ? "Helvetica" : "Courier";
    const suffix = bold && italic ? "-BoldOblique" : bold ? "-Bold" : italic ? "-Oblique" : "";
    return `${root}${suffix}`;
  }
  if (family === "times") {
    return bold && italic
      ? "Times-BoldItalic"
      : bold
        ? "Times-Bold"
        : italic
          ? "Times-Italic"
          : "Times-Roman";
  }
  return null;
}

const heading = (
  size: number,
  o: Partial<MdHeadingStyle> = {}
): MdHeadingStyle => ({
  family: "helvetica",
  size,
  color: "#1a1a1a",
  bold: true,
  italic: false,
  lineHeight: 1.25,
  align: "left",
  spaceBefore: Math.round(size * 0.9),
  spaceAfter: Math.round(size * 0.45),
  rule: false,
  ruleColor: "#d0d7de",
  ...o,
});

const defaultHeadings = (
  o: Partial<MdHeadingStyle> = {}
): MdThemeConfig["headings"] => [
  heading(26, o),
  heading(20, o),
  heading(16, o),
  heading(13.5, o),
  heading(12, o),
  heading(11, { ...o, color: o.color ?? "#57606a" }),
];

/** The shared skeleton presets are built from — “Clean” verbatim. */
const CLEAN: MdThemeConfig = {
  page: {
    preset: "A4",
    landscape: false,
    marginTop: 64,
    marginRight: 64,
    marginBottom: 64,
    marginLeft: 64,
    background: "#ffffff",
  },
  body: {
    family: "helvetica",
    size: 10.5,
    color: "#24292f",
    bold: false,
    italic: false,
    lineHeight: 1.5,
    align: "left",
    spaceBefore: 0,
    spaceAfter: 8,
  },
  headings: defaultHeadings(),
  code: {
    family: "courier",
    size: 9,
    color: "#1f2328",
    lineHeight: 1.45,
    background: "#f6f8fa",
    borderColor: null,
    padding: 10,
    spaceBefore: 4,
    spaceAfter: 10,
  },
  codespan: { family: "courier", color: "#c7254e", background: "#f6f8fa" },
  blockquote: {
    barColor: "#d0d7de",
    barWidth: 3,
    background: null,
    color: "#57606a",
    italic: false,
    indent: 14,
    spaceBefore: 4,
    spaceAfter: 10,
  },
  list: { indent: 18, markerColor: "", bullets: "•◦▪", itemGap: 3 },
  table: {
    size: 9.5,
    borderColor: "#d0d7de",
    borderWidth: 0.75,
    headerBackground: "#f6f8fa",
    headerColor: "#1f2328",
    headerBold: true,
    stripeBackground: null,
    cellPaddingX: 7,
    cellPaddingY: 5,
    spaceBefore: 4,
    spaceAfter: 10,
  },
  hr: { color: "#d0d7de", width: 1, spaceBefore: 12, spaceAfter: 12 },
  link: { color: "#0969da", underline: false },
  image: { align: "center", maxWidth: 1 },
};

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** Deep-merge helper for preset variations (arrays replaced whole). */
function themed(patch: {
  page?: Partial<MdPageStyle>;
  body?: Partial<MdTextStyle>;
  headings?: MdThemeConfig["headings"];
  code?: Partial<MdCodeStyle>;
  codespan?: Partial<MdCodespanStyle>;
  blockquote?: Partial<MdBlockquoteStyle>;
  list?: Partial<MdListStyle>;
  table?: Partial<MdTableStyle>;
  hr?: Partial<MdHrStyle>;
  link?: Partial<MdLinkStyle>;
  image?: Partial<MdImageStyle>;
}): MdThemeConfig {
  const base = clone(CLEAN);
  return {
    page: { ...base.page, ...patch.page },
    body: { ...base.body, ...patch.body },
    headings: patch.headings ?? base.headings,
    code: { ...base.code, ...patch.code },
    codespan: { ...base.codespan, ...patch.codespan },
    blockquote: { ...base.blockquote, ...patch.blockquote },
    list: { ...base.list, ...patch.list },
    table: { ...base.table, ...patch.table },
    hr: { ...base.hr, ...patch.hr },
    link: { ...base.link, ...patch.link },
    image: { ...base.image, ...patch.image },
  };
}

export const MD_THEMES: readonly MdThemePreset[] = [
  {
    id: "clean",
    label: "Clean",
    blurb: "Neutral sans-serif, generous whitespace",
    config: clone(CLEAN),
  },
  {
    id: "github",
    label: "GitHub",
    blurb: "The familiar README look, rules under h1/h2",
    config: themed({
      body: { family: "lib:opensans", size: 10, color: "#1f2328" },
      headings: [
        heading(24, { family: "lib:opensans", color: "#1f2328", rule: true, spaceAfter: 10 }),
        heading(18, { family: "lib:opensans", color: "#1f2328", rule: true, spaceAfter: 8 }),
        heading(14.5, { family: "lib:opensans", color: "#1f2328" }),
        heading(12, { family: "lib:opensans", color: "#1f2328" }),
        heading(10.5, { family: "lib:opensans", color: "#1f2328" }),
        heading(10, { family: "lib:opensans", color: "#57606a" }),
      ],
      codespan: { color: "#1f2328" },
      link: { color: "#0969da" },
      table: { stripeBackground: "#f6f8fa" },
    }),
  },
  {
    id: "serif",
    label: "Classic Serif",
    blurb: "Traditional report typography, justified body",
    config: themed({
      page: { marginTop: 72, marginRight: 78, marginBottom: 72, marginLeft: 78 },
      body: {
        family: "lib:libserif",
        size: 11,
        color: "#1c1917",
        lineHeight: 1.55,
        align: "justify",
        spaceAfter: 9,
      },
      headings: [
        heading(24, { family: "lib:libserif", color: "#1c1917", rule: true, ruleColor: "#a8a29e" }),
        heading(18, { family: "lib:libserif", color: "#1c1917" }),
        heading(14.5, { family: "lib:libserif", color: "#1c1917" }),
        heading(12.5, { family: "lib:libserif", color: "#1c1917", italic: true }),
        heading(11.5, { family: "lib:libserif", color: "#44403c", italic: true }),
        heading(11, { family: "lib:libserif", color: "#57534e", italic: true }),
      ],
      blockquote: { barColor: "#a8a29e", color: "#44403c", italic: true },
      table: { headerBackground: "#f5f5f4", borderColor: "#d6d3d1" },
      hr: { color: "#a8a29e" },
      link: { color: "#7c2d12", underline: true },
      codespan: { color: "#7c2d12" },
    }),
  },
  {
    id: "editorial",
    label: "Editorial",
    blurb: "Display headings over serif body, teal accents",
    config: themed({
      page: { marginTop: 76, marginRight: 70, marginBottom: 70, marginLeft: 70 },
      body: {
        family: "lib:libserif",
        size: 10.5,
        color: "#292524",
        lineHeight: 1.6,
        spaceAfter: 9,
      },
      headings: [
        heading(28, { family: "lib:montserrat", color: "#0f766e", spaceBefore: 26, spaceAfter: 12 }),
        heading(19, { family: "lib:montserrat", color: "#0f766e" }),
        heading(15, { family: "lib:montserrat", color: "#134e4a" }),
        heading(12.5, { family: "lib:montserrat", color: "#134e4a" }),
        heading(11, { family: "lib:montserrat", color: "#292524" }),
        heading(10.5, { family: "lib:montserrat", color: "#57534e" }),
      ],
      blockquote: { barColor: "#0f766e", barWidth: 2.5, background: "#f0fdfa", color: "#134e4a", italic: true },
      code: { background: "#f5f5f4" },
      codespan: { color: "#0f766e", background: "#f0fdfa" },
      table: { headerBackground: "#0f766e", headerColor: "#ffffff", borderColor: "#99f6e4" },
      hr: { color: "#0f766e", width: 1.5 },
      link: { color: "#0f766e", underline: true },
      list: { markerColor: "#0f766e" },
    }),
  },
  {
    id: "compact",
    label: "Compact",
    blurb: "Small type, tight spacing — more per page",
    config: themed({
      page: { marginTop: 40, marginRight: 44, marginBottom: 40, marginLeft: 44 },
      body: { size: 9, lineHeight: 1.35, spaceAfter: 5 },
      headings: [
        heading(17, { spaceBefore: 12, spaceAfter: 5 }),
        heading(13.5, { spaceBefore: 10, spaceAfter: 4 }),
        heading(11.5, { spaceBefore: 8, spaceAfter: 3 }),
        heading(10, { spaceBefore: 7, spaceAfter: 3 }),
        heading(9.5, { spaceBefore: 6, spaceAfter: 3 }),
        heading(9, { spaceBefore: 6, spaceAfter: 3, color: "#57606a" }),
      ],
      code: { size: 7.5, padding: 7, spaceAfter: 7 },
      table: { size: 8, cellPaddingX: 5, cellPaddingY: 3, spaceAfter: 7 },
      blockquote: { indent: 10, spaceAfter: 7 },
      list: { indent: 14, itemGap: 2 },
      hr: { spaceBefore: 8, spaceAfter: 8 },
    }),
  },
  {
    id: "typewriter",
    label: "Typewriter",
    blurb: "Monospace everything, plain-text soul",
    config: themed({
      body: { family: "lib:libmono", size: 9.5, color: "#262626", lineHeight: 1.5 },
      headings: [
        heading(18, { family: "lib:libmono", color: "#171717" }),
        heading(14, { family: "lib:libmono", color: "#171717" }),
        heading(12, { family: "lib:libmono", color: "#171717" }),
        heading(10.5, { family: "lib:libmono", color: "#171717" }),
        heading(10, { family: "lib:libmono", color: "#404040" }),
        heading(9.5, { family: "lib:libmono", color: "#525252" }),
      ],
      code: { family: "lib:libmono", background: "#f5f5f5", borderColor: "#d4d4d4" },
      codespan: { family: "lib:libmono", color: "#262626", background: "#ededed" },
      blockquote: { barColor: "#a3a3a3", color: "#525252" },
      table: { headerBackground: "#ededed", borderColor: "#c9c9c9" },
      link: { color: "#1d4ed8", underline: true },
      hr: { color: "#a3a3a3" },
      list: { bullets: "-*+" },
    }),
  },
];

export const cloneTheme = (c: MdThemeConfig): MdThemeConfig => clone(c);
